import { getShippingPointName } from '../../pages/shipment/ShippingPoints'
import {
  EnrichedDeliveryNote,
  GoodsHolder,
  Measure,
  Shipment,
  ShipmentVendor,
  ShipTo,
} from '../../generated/graphql'
import { ShipmentLink } from '../components/links/Links'
import { Icon, Text, Tooltip } from '@nike/eds'
import { Placement } from '@popperjs/core'
import { format as formatFns, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import TooltipDeliveryNoteShipToContent from '../components/tooltipcontent/tooltipdeliverynoteshiptocontent/TooltipDeliveryNoteShipToContent'
import TooltipCmrContent from '../components/tooltipcontent/tooltipcmrcontent/TooltipCmrContent'
import TooltipCancelledShipmentContent from '../components/tooltipcontent/tooltipcancelledshipmentcontent/TooltipCancelledShipmentContent'
import { GeneralTooltip } from '../components/generaltooltip/GeneralTooltip'
import TooltipPacklistShipToContent from '../components/tooltipcontent/tooltippacklistshiptocontent/TooltipPacklistShipToContent'
import TooltipCarrierContent from '../components/tooltipcontent/tooltipcarriercontent/TooltipCarrierContent'

export function format(formatFunc: (value: any) => any, ...extraArgs: []) {
  return ({ value }) => formatFunc(value, ...extraArgs)
}

export interface Formatter {
  (data: any, options?: { [key: string]: any }): any
}

export function shipmentLink(packlistNumber: string) {
  return <ShipmentLink packlistNumber={packlistNumber} />
}

export function shipmentLinks(packlistNumbers: string[]) {
  return packlistNumbers.map((p) => <ShipmentLink packlistNumber={p} />)
}

export function carrierAndHub(vendor: ShipmentVendor) {
  return vendor ? (
    <GeneralTooltip
      body={<TooltipCarrierContent hubCode={vendor.hubCode} vendorCode={vendor.vendorCode} />}
      anchorElement={
        <span className="vcenter">
          {`${vendor.vendorCode} / ${vendor.hubCode}`}{' '}
          <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
        </span>
      }
    />
  ) : null
}

export function orderCarrierAndHub(order: EnrichedDeliveryNote) {
  const carrier = order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestParty.find(
    (p) => p.partyTypeCode === 'CARRIER'
  )

  return carrier ? (
    <GeneralTooltip
      body={
        <TooltipCarrierContent
          hubCode={carrier.fulfillmentRequestLocationAddress.transportationZone}
          vendorCode={carrier.partyIdentifier}
        />
      }
      anchorElement={
        <span className="vcenter">
          {`${carrier.partyIdentifier} / ${carrier.fulfillmentRequestLocationAddress.transportationZone}`}{' '}
          <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
        </span>
      }
    />
  ) : null
}

export function plannedAndActualGoodsHolderCount(shipment: Shipment) {
  return `${shipment.plannedGoodsHolderCount ?? ''} / ${shipment.calculatedGoodsHolderCount ?? ''}`
}

export function plannedAndActualPackedQuantityForShipment(shipment: Shipment) {
  return `${shipment.calculatedPlannedQuantity ?? ''} / ${shipment.calculatedPackedQuantity ?? ''}`
}

export function plannedAndActualPackedQuantity(goodsHolder: GoodsHolder) {
  return `${goodsHolder.calculatedPlannedQuantity ?? ''} / ${
    goodsHolder.calculatedPackedQuantity ?? ''
  }`
}

export function plannedUnits(order: EnrichedDeliveryNote) {
  let units = 0
  order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestItem.map(
    (i) => (units += i.salesUnitQuantity)
  )

  return units
}

export function trackAndTraceExceptionIcon(showException: boolean) {
  return showException ? <Icon name="AlertCircle" size="s" /> : ''
}

export function date(date: string, { showTime = true } = {}) {
  if (!date) {
    return ''
  }
  if (showTime && date.includes('T')) {
    return formatFns(parseISO(date), 'dd/MM/yyyy HH:mm')
  } else {
    return formatFns(parseISO(date), 'dd/MM/yyyy')
  }
}

function isSameDateTime(dateFrom: string, dateTo: string) {
  return formatDate(dateFrom) === formatDate(dateTo)
}

function isSameDateButDifferentTime(dateFrom: string, dateTo: string) {
  return (
    formatShortDate(dateFrom) === formatShortDate(dateTo) &&
    formatTimeSimple(dateFrom) !== formatTimeSimple(dateTo)
  )
}

export function formatShortDate(date: string, cet = false) {
  const ISODate = parseISO(date)
  const format = 'dd/MM/yyyy'
  return cet ? formatInTimeZone(ISODate, 'Europe/Brussels', format) : formatFns(ISODate, format)
}

export function formatTimeSimple(date: string, cet = false) {
  if (!date) {
    return date
  }
  const ISODate = parseISO(date)
  const format = 'HH:mm'
  return cet ? formatInTimeZone(ISODate, 'Europe/Brussels', format) : formatFns(ISODate, format)
}

function formatDate(date: string, cet = false) {
  const ISODate = parseISO(date)
  const format = 'dd/MM/yyyy HH:mm'
  return cet ? formatInTimeZone(ISODate, 'Europe/Brussels', format) : formatFns(ISODate, format)
}

export function idpDateTimeWindow(dateFrom: string, dateTo: string) {
  if (isSameDateTime(dateFrom, dateTo)) {
    return formatDate(dateFrom, true)
  } else if (isSameDateButDifferentTime(dateFrom, dateTo)) {
    return `${formatDate(dateFrom, true)} - ${formatTimeSimple(dateTo, true)}`
  } else {
    return `${dateFrom ? formatDate(dateFrom, true) : ''} - ${
      dateTo ? formatDate(dateTo, true) : ''
    }`
  }
}

export function idpDate(date: string) {
  if (!date) {
    return ''
  }
  return formatShortDate(date, true)
}

export function milestoneDate(date: string, { showTime = true } = {}) {
  if (!date) {
    return ''
  }
  if (showTime && date.includes('T')) {
    return formatDate(date)
  } else {
    return formatShortDate(date)
  }
}

export function formattedDate(date: string, dateFormat) {
  if (!date) {
    return ''
  }
  return dateFormat(date)
}

export function bool(value: any) {
  return value ? '✔' : ''
}

export function list(value: any[], { separator = ', ', format = defaultFormatter } = {}) {
  return value
    ? value
        .flat(Infinity)
        .map((it: any) => format(it))
        .join(separator)
    : ''
}

export function defaultFormatter(value: any) {
  if (Array.isArray(value)) {
    return list(value)
  }
  if (value && typeof value === 'object') {
    return value.toString()
  }
  if (typeof value === 'boolean') {
    return bool(value)
  }

  return value === null || value === undefined ? '' : value
}

export const shipStatusMapping = {
  CREATED: 'Created',
  PARTIALLY_LOADED: 'Partially loaded',
  LOADED: 'Loaded',
  SHIPPED: 'Shipped',
  PARTIALLY_DELIVERED: 'Partially delivered',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  COMPLETELY_DELIVERED: 'Completely Delivered',
}

export const rebookingTypeMapping = {
  HOLD: 'Hold',
  REBOOKING: 'Rebooked',
}

export function shipmentStatus(status: string) {
  return status ? shipStatusMapping[status] || status : ''
}

export function shipmentStatusTooltip(shipment: Shipment) {
  if (showCancelledTooltip(shipment)) {
    return (
      <GeneralTooltip
        body={<TooltipCancelledShipmentContent data={shipment} />}
        anchorElement={
          <span className="vcenter">
            {capitalize(shipment.calculatedShipmentStatusCode)?.replace('_', ' ')}
            <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
          </span>
        }
      />
    )
  } else if (shipment && shipment?.calculatedShipmentStatusCode) {
    return (
      <span className="vcenter">
        {capitalize(shipment.calculatedShipmentStatusCode)?.replace('_', ' ')}
      </span>
    )
  }
  return null
}

function showCancelledTooltip(shipment: Shipment) {
  return (
    shipment &&
    shipment?.calculatedShipmentStatusCode &&
    shipment?.calculatedShipmentStatusCode === 'CANCELLED' &&
    ((shipment?.shipmentTexts && shipment?.shipmentTexts[0]?.textDescription) ||
      shipment?.updatedByIdentifier ||
      shipment?.shipmentCancelTimestamp)
  )
}

export function rebookingType(type: keyof typeof rebookingTypeMapping | undefined) {
  return rebookingTypeMapping[type] || 'No'
}

export function shipTo(shipTo: ShipTo) {
  return shipTo ? `${shipTo.shipToCode || ''} / ${shipTo.shipToName || ''}` : ''
}

export function cmrDataTooltip(shipment: Shipment) {
  if (shipment && shipment.__typename && shipment.proNumbers && shipment.proNumbers.length > 0) {
    return (
      <GeneralTooltip
        body={<TooltipCmrContent data={shipment} />}
        anchorElement={
          <span className="vcenter">
            {UniqueTruncatedTooltip(shipment.proNumbers, 'bottom-start')}
            <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
          </span>
        }
      />
    )
  }
  return null
}

export function shipToTooltip(shipTo: ShipTo) {
  //cant use hasCustomerdetails, implemented hacky solution
  if (
    shipTo &&
    Object.keys(shipTo).length > 3 &&
    shipTo.__typename &&
    shipTo.shipToCode &&
    shipTo.shipToName
  ) {
    return (
      <GeneralTooltip
        body={<TooltipPacklistShipToContent data={shipTo} />}
        anchorElement={
          <span className="vcenter">
            {`${shipTo?.shipToCode || ''} / ${shipTo?.shipToName || ''}`}
            <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
          </span>
        }
      />
    )
  }
  return (
    <span className="vcenter">{`${shipTo?.shipToCode || ''} / ${shipTo?.shipToName || ''}`}</span>
  )
}

export function orderShipToNumber(order: EnrichedDeliveryNote) {
  const shipTo = order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestParty?.find(
    (p) => p.partyTypeCode === 'SHIP_TO'
  )

  return shipTo ? shipTo.partyIdentifier : ''
}

export function orderShipToName(order: EnrichedDeliveryNote) {
  const shipTo = order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestParty?.find(
    (p) => p.partyTypeCode === 'SHIP_TO'
  )

  return shipTo ? shipTo.partyName1 : ''
}

export function orderShipTo(order: EnrichedDeliveryNote) {
  const shipTo = order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestParty?.find(
    (p) => p.partyTypeCode === 'SHIP_TO'
  )

  return shipTo ? `${shipTo.partyIdentifier || ''} / ${shipTo.partyName1 || ''}` : ''
}

export function orderShipToTooltip(order: EnrichedDeliveryNote) {
  const shipTo = order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestParty?.find(
    (p) => p.partyTypeCode === 'SHIP_TO'
  )

  //cant use hasCustomerdetails, implemented hacky solution
  if (
    shipTo &&
    Object.keys(shipTo).length > 5 &&
    shipTo.__typename &&
    shipTo.partyName1 &&
    shipTo.partyIdentifier &&
    shipTo.partyTypeCode &&
    shipTo.fulfillmentRequestLocationAddress
  ) {
    return (
      <GeneralTooltip
        body={<TooltipDeliveryNoteShipToContent data={order} />}
        anchorElement={
          <span className="vcenter">
            {`${shipTo.partyIdentifier || ''} / ${shipTo?.partyName1 || ''}`}
            <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
          </span>
        }
      />
    )
  }
  return (
    <span className="vcenter">
      {`${shipTo.partyIdentifier || ''} / ${shipTo?.partyName1 || ''}`}
    </span>
  )
}

export function orderCmrDataTooltip(order: EnrichedDeliveryNote) {
  console.log(order)
  if (order && order.shipments && order.shipments.length > 0) {
    const shipment = order.shipments[0]
    const cmrData = {
      vendorAccessNumbers: shipment.vendorAccessNumbers,
      transportHolderNumbers: shipment.transportHolderNumbers,
      calculatedCmrShipmentCount: shipment.calculatedCmrShipmentCount,
      calculatedCmrGoodsHolderCount: shipment.calculatedCmrGoodsHolderCount,
      shippingPointCode: shipment.shippingPointCode,
      plannedShipmentReadyTimestamp: shipment.plannedShipmentReadyTimestamp,
      transportHolderLicensePlateNumbers: shipment.transportHolderLicensePlateNumbers,
      transportHolderSealNumbers: shipment.transportHolderSealNumbers,
      transportHolderTypeSizeCodes: shipment.transportHolderTypeSizeCodes,
      transportHolderGrossVolume: shipment.transportHolderGrossVolume,
    }
    return (
      <GeneralTooltip
        body={<TooltipCmrContent data={cmrData} />}
        anchorElement={
          <span className="vcenter">
            {UniqueTruncatedTooltip(order.shipments[0].proNumbers, 'bottom-start')}
            <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
          </span>
        }
      />
    )
  }
  return null
}

export function orderPurchaseOrderNumber(order: EnrichedDeliveryNote) {
  const purchaseOrderNumber =
    order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestReference.find(
      (r) => r.referenceNumberTypeCode === 'INITIATING_DOCUMENT_NUMBER'
    )

  return purchaseOrderNumber?.referenceNumberValue
}

export function orderSalesOrderNumber(order: EnrichedDeliveryNote) {
  const salesOrderNumber =
    order.deliveryNote.fulfillmentRequestHeader.fulfillmentRequestReference.find(
      (r) => r.referenceNumberTypeCode === 'SALES_DOCUMENT_NUMBER'
    )

  return salesOrderNumber?.referenceNumberValue
}

export function shippingPoint(shippingPoint: number) {
  return shippingPoint ? `${shippingPoint} / ${getShippingPointName(shippingPoint)}` : ''
}

export function measure(measure: Measure) {
  return `${measure?.measureValue || ''} ${measure?.measureUOM?.toLowerCase() || ''}`
}

export function capitalize(word: string) {
  return word.length > 0 ? word[0].toLocaleUpperCase() + word.substring(1).toLocaleLowerCase() : ''
}

export function truncate(s: string, maxLength = 30) {
  if (s?.length > maxLength) {
    let truncated = s.slice(0, maxLength)
    return truncated.slice(0, truncated.lastIndexOf(' ')) + ' ...'
  }
  return s
}

export function unique(arr: any[], options?: { [key: string]: any }) {
  const sort = options?.sort
  const separator = options?.separator || ', '
  const format = options?.format || defaultFormatter
  const uniquelist = [...new Set(arr)].sort(sort)
  return list(uniquelist, { separator, format })
}

export function UniqueTruncatedTooltip(data: any, tooltipPlacement: Placement = 'right') {
  const stringVal = String(unique(data))

  return (
    <Tooltip bodySlot={<Text>{stringVal}</Text>} isDark={true} placement={tooltipPlacement}>
      <Text
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          maxWidth: '150px',
          pointerEvents: 'none',
        }}
      >
        {stringVal}
      </Text>
    </Tooltip>
  )
}
